import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './ImageSliderItem.module.scss';
import { setupTransitionInTimeline, scaleImage } from './ImageSliderItem.transitions';
import type { ImageSliderItemProps, ImageSliderItemRef } from './ImageSliderItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import Image from '../../../atoms/image/Image';
import { NODE_HEADING, NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import reactToString from 'react-to-string';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import { getLink } from '../../../../util/url.utils';
import { useRouter } from 'next/router';
import PrimaryLink from 'components/atoms/primary-link/PrimaryLink';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';
import Link from 'next/link';
import { useCanPlayWebmContext } from '../../../../context/canPlayWebmContext';

export default function ImageSliderItem({
  includeImage,
  blok,
  transitionInDelay,
  figCaptionMaxHeight,
  ref,
  ...props
}: ImageSliderItemProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<ImageSliderItemRef | null>)?.current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const imageRef = useRef<ImageRef>(null);
  const labelRef = useRef<HeadingRef>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const jobTitleRef = useRef<TextRef>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<PrimaryLinkRef>(null);

  const { canPlayWebm } = useCanPlayWebmContext();

  const router = useRouter();
  let link: string | undefined;
  if (blok?.buttons) {
    link = getLink(blok?.buttons[0]?.link, router.locale);
  }

  useImperativeHandle(
    ref,
    () => ({
      imageRef,
      elementRef,
    }),
    [imageRef, elementRef],
  );

  useScrollTransition({
    scroll: {
      transitionInDelay,
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imageRef,
        labelRef,
        jobTitleRef,
        imageHolderRef,
        imageContainerRef,
        linkRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    scaleImage(imageRef, isHover);
  }, [isHover]);

  const onMouseEnter = useCallback(() => {
    setIsHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setTimeout(() => {
      setIsHover(false);
    }, 0);
  }, []);

  const Wrapper = link ? Link : 'div';

  return (
    <Wrapper
      // @ts-ignore
      href={link}
      className={classNames(props.className, styles.imageSliderItem, { [styles.link]: link })}
      // @ts-ignore
      ref={elementRef}
    >
      {includeImage ? (
        <figure className={classNames(styles.parentFigure)}>
          {blok.image.filename && (
            <div
              ref={imageHolderRef}
              className={classNames(styles.imageHolder, { [styles.pointer]: isHover })}
              onMouseEnter={link ? onMouseEnter : undefined}
              onMouseLeave={link ? onMouseLeave : undefined}
            >
              <div ref={imageContainerRef} className={classNames(styles.imageContainer)}>
                {blok?.animated_image?.filename && canPlayWebm ? (
                  <video
                    src={blok.animated_image.filename}
                    autoPlay
                    muted
                    controls={false}
                    playsInline
                    loop
                    className={classNames(styles.animatedImage)}
                  />
                ) : (
                  <Image
                    ref={imageRef}
                    src={blok.image.filename}
                    srcDesktop={blok.image.filename}
                    className={classNames(styles.image)}
                    layout="responsive"
                    objectFit="cover"
                    alt={blok.image.alt}
                  />
                )}
              </div>
            </div>
          )}

          {blok.caption && (
            <figcaption
              className={styles.caption}
              style={figCaptionMaxHeight ? { height: figCaptionMaxHeight } : undefined}
            >
              {render(blok.caption, {
                nodeResolvers: {
                  [NODE_HEADING]: (children) => (
                    <Heading
                      ref={labelRef}
                      as="h6"
                      size="label-large"
                      transitionInType="splitWordsSlideInUp"
                      disableTriggerInOnScroll
                    >
                      {children}
                    </Heading>
                  ),
                  [NODE_PARAGRAPH]: (children) => (
                    <Text
                      ref={jobTitleRef}
                      className={styles.jobTitle}
                      as="p"
                      size="body-standard"
                      disableTriggerInOnScroll
                    >
                      {children}
                    </Text>
                  ),
                },
              })}
              {blok?.buttons && blok?.buttons[0] && link && (
                <PrimaryLink
                  ref={linkRef}
                  textVariant="button-label-small"
                  className={classNames(styles.button)}
                  link={{ href: link }}
                  label={blok?.buttons[0].label}
                  disableTriggerInOnScroll
                  transitionInType="splitLineRotateIn"
                  isActive={isHover}
                />
              )}
            </figcaption>
          )}
        </figure>
      ) : (
        <figcaption
          ref={elementRef}
          className={classNames(styles.imageSliderItem, props.className)}
        >
          {render(blok.caption, {
            nodeResolvers: {
              [NODE_HEADING]: (children) => (
                <Heading
                  ref={labelRef}
                  as="h5"
                  size="heading-05"
                  transitionInType="splitWordsSlideInUp"
                  className={styles.label}
                  disableTriggerInOnScroll
                >
                  {reactToString(children)}
                </Heading>
              ),
              [NODE_PARAGRAPH]: (children) => (
                <Text
                  ref={jobTitleRef}
                  className={styles.jobTitle}
                  as="p"
                  size="body-standard"
                  disableTriggerInOnScroll
                >
                  {children}
                </Text>
              ),
            },
          })}

          {blok?.buttons && blok?.buttons[0] && link && (
            <PrimaryLink
              ref={linkRef}
              textVariant="button-label-small"
              className={classNames(styles.button)}
              link={{ href: link }}
              label={blok?.buttons[0].label}
              disableTriggerInOnScroll
              transitionInType="splitLineRotateIn"
            />
          )}
        </figcaption>
      )}
    </Wrapper>
  );
}
